import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Package, FilePdf, Ruler, Info, ShieldCheck, CheckCircle } from "@phosphor-icons/react";
import FactoryVideoGallery from "@/components/FactoryVideoGallery";
import ComparisonScorecard from "@/components/ComparisonScorecard";
import CompetitorTable from "@/components/CompetitorTable";
import ProductHighlights from "@/components/ProductHighlights";
import StonLogo from "@/components/StonLogo";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function PublicProductSpecs() {
  const { productId } = useParams();
  const [data, setData] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [fileError, setFileError] = useState("");
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${BASE}/api/public/product/${productId}`, { timeout: 15000 });
        if (alive) setData(res.data);
      } catch {
        if (alive) setError(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [productId]);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${BASE}/api/public/product/${productId}/comparison`, { timeout: 15000 })
      .then((res) => { if (alive) setComparison(res.data); })
      .catch(() => { if (alive) setComparison(null); });
    return () => { alive = false; };
  }, [productId]);

  const fileUrl = useCallback(
    (path) => `${BASE}/api/public/product/${productId}/file/${path}`,
    [productId]
  );

  // Fetch a document as a blob and open the object URL (matching image loading),
  // with retry + backoff on a short timeout and a clear error message.
  const openFile = useCallback(
    async (path) => {
      setFileError("");
      const win = window.open("", "_blank");
      const url = `${BASE}/api/public/product/${productId}/file/${path}`;
      let delay = 400;
      for (let attempt = 1; attempt <= 3; attempt += 1) {
        try {
          const res = await axios.get(url, { responseType: "blob", timeout: 15000 });
          const objectUrl = URL.createObjectURL(res.data);
          if (win) win.location = objectUrl;
          else window.location.assign(objectUrl);
          setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
          return;
        } catch {
          if (attempt === 3) {
            if (win) win.close();
            setFileError("Couldn't open the file — the storage service may be busy. Please try again.");
            setTimeout(() => setFileError(""), 6000);
            return;
          }
          await new Promise((r) => setTimeout(r, delay));
          delay *= 2;
        }
      }
    },
    [productId]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] grid place-items-center">
        <div className="h-10 w-10 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] grid place-items-center px-6 text-center">
        <div>
          <Package size={48} weight="duotone" className="mx-auto text-zinc-300" />
          <div className="mt-3 text-zinc-600">This product spec sheet isn't available.</div>
        </div>
      </div>
    );
  }

  const images = [data.image_path, ...(data.gallery || []).map((g) => g.image_path)].filter(Boolean);
  const docs = (data.documents || []).filter((d) => d.pdf_path);

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="public-specs-page">
      {fileError && (
        <div
          data-testid="public-specs-file-error"
          className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-[#B22222] text-white text-sm px-4 py-3 shadow-lg rounded max-w-[92vw]"
        >
          {fileError}
        </div>
      )}

      {/* Official document header */}
      <header className="bg-zinc-900 text-white">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-5 flex items-center justify-between gap-4">
          <div>
            <StonLogo size={20} subtitle="Building Materials" className="text-white" />
          </div>
          <div className="text-right">
            <div className="text-[0.65rem] uppercase tracking-[0.2em] text-white/50">
              Material Specification
            </div>
            <div className="font-mono-mt text-sm" data-testid="public-specs-ref">
              {data.brand || data.category_label || "Product"}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 md:px-8 py-8 md:py-12">
        <section className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
          {/* Gallery */}
          <div>
            <div className="relative aspect-square bg-zinc-100 border border-zinc-200 overflow-hidden">
              {images[activeImage] ? (
                <img
                  src={fileUrl(images[activeImage])}
                  alt={data.name}
                  data-testid="public-specs-image"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 grid place-items-center">
                  <Package size={64} weight="duotone" className="text-zinc-300" />
                </div>
              )}
            </div>
            {images.length > 1 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {images.map((path, i) => (
                  <button
                    key={path}
                    onClick={() => setActiveImage(i)}
                    className={`w-16 h-16 overflow-hidden border transition-colors ${
                      i === activeImage ? "border-zinc-900" : "border-zinc-200 hover:border-zinc-400"
                    }`}
                  >
                    <img src={fileUrl(path)} alt={`view ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Identity + specs (NO price, NO request-quote) */}
          <div>
            <div className="mt-tiny text-[#8B4513] uppercase tracking-[0.18em] text-xs">
              {data.brand || data.category_label || "Material"}
            </div>
            <h1
              className="font-display text-3xl md:text-4xl font-black tracking-tight text-zinc-900 mt-2 leading-[1.1]"
              data-testid="public-specs-name"
            >
              {data.name}
            </h1>
            {data.description && (
              <p className="mt-4 text-sm md:text-base text-zinc-700 leading-relaxed">{data.description}</p>
            )}

            <ProductHighlights highlights={data.highlights} />

            <div className="grid grid-cols-2 gap-3 mt-6">
              {data.category_label && <Meta label="Category" value={data.category_label} />}
              {data.lead_time && <Meta label="Lead time" value={data.lead_time} />}
              {data.sizes && <Meta label="Sizes" value={data.sizes} />}
              <Meta label="Supplied by" value="Ston Link" />
            </div>
          </div>
        </section>

        {/* Options */}
        {(data.materials?.length > 0 || data.option_sizes?.length > 0 || data.finishes?.length > 0) && (
          <section className="mt-10 bg-white border border-zinc-200 p-5 md:p-7">
            <SectionTitle icon={<Ruler size={16} weight="duotone" />} title="Specifications & options" />
            <PillRow label="Materials" items={data.materials} />
            <PillRow label="Sizes" items={data.option_sizes} />
            {data.finishes?.length > 0 && (
              <div className="mt-4">
                <div className="text-sm text-zinc-500 mb-2">Finishes</div>
                <div className="flex flex-wrap gap-2">
                  {data.finishes.map((f) => (
                    <span
                      key={f.name}
                      className="inline-flex items-center gap-2 px-3 py-1.5 border border-zinc-200 text-sm text-zinc-800"
                    >
                      <span
                        className="w-3.5 h-3.5 rounded-full border border-zinc-300"
                        style={{ backgroundColor: f.hex || "#d4d4d8" }}
                      />
                      {f.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Drawings, certificates & documents */}
        {docs.length > 0 && (
          <section className="mt-10" data-testid="public-specs-documents">
            <SectionTitle
              icon={<ShieldCheck size={16} weight="duotone" />}
              title="Technical drawings & certificates"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
              {docs
                .slice()
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((d) => (
                  <button
                    type="button"
                    key={d.doc_id}
                    onClick={() => openFile(d.pdf_path)}
                    data-testid={`public-specs-doc-${d.doc_id}`}
                    className="group flex flex-col items-center gap-2 text-center hover:-translate-y-0.5 transition-transform"
                  >
                    <div className="w-full aspect-square border border-zinc-200 bg-white grid place-items-center overflow-hidden group-hover:border-[#0047AB]">
                      {d.icon_path ? (
                        <img src={fileUrl(d.icon_path)} alt={d.name} className="w-full h-full object-contain p-2" />
                      ) : (
                        <FilePdf size={36} className="text-[#B22222]" weight="duotone" />
                      )}
                    </div>
                    <div className="text-xs font-medium text-zinc-800 leading-tight line-clamp-2">{d.name}</div>
                  </button>
                ))}
            </div>
          </section>
        )}

        {/* Factory in Action */}
        <FactoryVideoGallery videos={data.videos} />

        <ComparisonScorecard
          data={comparison}
          renderImage={(p) =>
            p.image_path ? (
              <img
                src={`${BASE}/api/public/product/${p.product_id}/file/${p.image_path}`}
                alt={p.name}
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 bg-zinc-100" />
            )
          }
        />

        <CompetitorTable
          benchmark={data.benchmark}
          datasheetUrl={(c) =>
            c.datasheet_path ? `${BASE}/api/public/product/${productId}/file/${c.datasheet_path}` : null
          }
        />

        {/* Verification banner */}
        <section className="mt-10 flex items-start gap-3 bg-[#0047AB]/5 border border-[#0047AB]/15 p-4 md:p-5">
          <CheckCircle size={22} weight="fill" className="text-[#0047AB] shrink-0 mt-0.5" />
          <div className="text-sm text-zinc-700">
            <span className="font-semibold text-zinc-900">Official material specification.</span>{" "}
            Issued by Ston Link — Building Materials for verification purposes. This document does
            not contain pricing.
          </div>
        </section>

        <footer className="mt-12 pt-6 border-t border-zinc-200 flex items-center gap-2 text-xs text-zinc-500">
          <Info size={14} />
          <span>Ston Link — Building Materials · Material specification sheet</span>
        </footer>
      </main>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div className="border border-zinc-200 bg-white p-3">
      <div className="mt-tiny text-zinc-400 uppercase text-[0.6rem] tracking-wider">{label}</div>
      <div className="text-sm font-medium text-zinc-900 mt-0.5">{value ?? "—"}</div>
    </div>
  );
}

function SectionTitle({ icon, title }) {
  return (
    <div className="flex items-center gap-2 text-[#0047AB] mb-4">
      {icon}
      <h2 className="font-display text-lg md:text-xl font-black tracking-tight text-zinc-900">{title}</h2>
    </div>
  );
}

function PillRow({ label, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-4 first:mt-0">
      <div className="text-sm text-zinc-500 mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        {items.map((it) => (
          <span key={it} className="px-3 py-1.5 border border-zinc-300 text-sm text-zinc-800">
            {it}
          </span>
        ))}
      </div>
    </div>
  );
}
